import { useCallback, useMemo } from 'react';
import { useFragment } from '@apollo/client';

import type { FlagDefinition } from '@occc/data-flags';
import { getRegisteredFlags } from '@occc/data-flags';
import { useMutation } from '@occc-app/dataAccessLayer/utils';
import {
  Custom_Data_Mode,
  OpenCommerceUserCustomDataFragment,
  OpenCommerceUserCustomDataFragmentDoc,
  useOpenCommerceUserUpdateMutation,
} from '@occc/graphql';

import {
  extractCommandCenter,
  isCommandCenterData,
  parseFeatureFlags,
} from './parse-command-center';

export type FeatureAccessFormValues = Record<string, boolean>;

// React Final Form interprets dots in field names as nested object paths.
// Flag keys like 'feature-flag-control.editor' must be escaped for form fields
// and unescaped when building the mutation payload.
export function toFieldName(flagKey: string): string {
  return flagKey.replace(/\./g, '___');
}

function toFlagKey(fieldName: string): string {
  return fieldName.replace(/___/g, '.');
}

type UseFeatureAccessFormResult = {
  flags: readonly FlagDefinition[];
  initialValues: FeatureAccessFormValues;
  onSubmit: (values: FeatureAccessFormValues) => Promise<void>;
};

export function useFeatureAccessForm(userUuid: string): UseFeatureAccessFormResult {
  const flags = useMemo(() => getRegisteredFlags(), []);

  const { data } = useFragment<OpenCommerceUserCustomDataFragment>({
    fragment: OpenCommerceUserCustomDataFragmentDoc,
    from: { __typename: 'OpenCommerceUser', uuid: userUuid },
  });

  const initialValues = useMemo<FeatureAccessFormValues>(() => {
    const commandCenter = extractCommandCenter(data?.customData);
    const snapshot = parseFeatureFlags(commandCenter);
    const values: FeatureAccessFormValues = {};

    for (const flag of flags) {
      values[toFieldName(flag.key)] = snapshot[flag.key] ?? flag.defaultValue;
    }

    return values;
  }, [data?.customData, flags]);

  const [updateUser] = useMutation(useOpenCommerceUserUpdateMutation);

  const onSubmit = useCallback(
    async (values: FeatureAccessFormValues) => {
      const features: Record<string, boolean> = {};

      for (const [fieldName, value] of Object.entries(values)) {
        features[toFlagKey(fieldName)] = Boolean(value);
      }

      // Preserve existing commandCenter keys (e.g. settings) when updating features.
      // The backend MERGE mode is shallow at the customData level, so it replaces
      // the entire commandCenter key. We must spread existing data to avoid loss.
      const existingCommandCenter = extractCommandCenter(data?.customData);
      const commandCenter = {
        ...(isCommandCenterData(existingCommandCenter) ? existingCommandCenter : {}),
        features,
      };

      await updateUser({
        variables: {
          input: {
            uuid: userUuid,
            customData: {
              mode: Custom_Data_Mode.Merge,
              data: { commandCenter },
            },
          },
        },
      });
    },
    [userUuid, updateUser, data?.customData],
  );

  return { flags, initialValues, onSubmit };
}
